import gql from 'graphql-tag';
import _ from 'lodash';
import { INSTRUCTOR_UNSCORED } from '../scoreReason';

const getScoresByActivityGQL = gql`
query getScoresByActivity($cohortId: UUID!, $deploymentId: UUID!, $activityId: UUID!) {
  cohortById(cohortId: $cohortId) {
    id
    enrollments {
      edges {
        node {
          account {
            id
          }
          learnerPerformanceByDeployment(deploymentId: $deploymentId, activityId: $activityId) {
            score(cohortId: $cohortId) {
              reason
              value
            }
            progress {
              id
              completion {
                value
              }
            }
            getLatestAttemptActivityManualGradeReport {
              attemptId
              componentId
              parentId
              parentType
              state
              studentId
              grades {
                createdAt
                score
              }
            }
          }
        }
      }
    }
  }
}
`;

const getScoresByInteractiveGQL = gql`
query getScoresByInteractive($cohortId: UUID!, $deploymentId: UUID!, $interactiveId: UUID!, $activityId: UUID!) {
  cohortById(cohortId: $cohortId) {
    id
    enrollments {
      edges {
        node {
          account {
            id
          }
          learnerPerformanceByDeployment(deploymentId: $deploymentId, activityId: $activityId) {
            getLatestAttemptActivityManualGradeReport {
              attemptId
              componentId
              parentId
              parentType
              state
              studentId
              grades {
                createdAt
                score
              }
            }
            progress {
              id
              completion {
                value
              }
            }
          }
          interactiveScore(deploymentId: $deploymentId, interactiveId: $interactiveId, cohortId: $cohortId) {
            reason
            value
          }
        }
      }
    }
  }
}
`;

export const queryGetScoresByActivity = async (apolloClient, { cohortId, deploymentId, activityId }) => {
  const response = await apolloClient.query({
    query: getScoresByActivityGQL,
    variables: {
      cohortId,
      deploymentId,
      activityId,
    },
    errorPolicy: 'all',
  });

  const doesActivityRequireGrade = (gradesList) => gradesList.some(({ state }) => (
    state === INSTRUCTOR_UNSCORED
  ));

  const mapEnrollment = ({
    account: { id },
    learnerPerformanceByDeployment: {
      score,
      progress,
      getLatestAttemptActivityManualGradeReport = [],
    },
  }) => ({
    id,
    score,
    progress,
    hasGradeables: getLatestAttemptActivityManualGradeReport.length > 0,
    requiresGrading: doesActivityRequireGrade(getLatestAttemptActivityManualGradeReport),
  });

  return _.chain(response)
    .get('data.cohortById.enrollments.edges')
    .map(({ node }) => mapEnrollment(node))
    .keyBy('id')
    .value();
};

export const queryGetScoresByInteractive = async (apolloClient, { cohortId, deploymentId, interactiveId, activityId }) => {
  const response = await apolloClient.query({
    query: getScoresByInteractiveGQL,
    variables: {
      cohortId,
      deploymentId,
      interactiveId,
      activityId,
    },
    errorPolicy: 'all',
  });

  const doesInteractiveRequireGrade = (gradesList) => gradesList
    .filter(({ parentId }) => parentId === interactiveId)
    .some(({ state }) => (state === INSTRUCTOR_UNSCORED));

  const mapEnrollment = ({
    account: { id },
    interactiveScore,
    learnerPerformanceByDeployment: {
      progress,
      getLatestAttemptActivityManualGradeReport = [],
    },
  }) => ({
    id,
    score: interactiveScore,
    progress,
    hasGradeables: getLatestAttemptActivityManualGradeReport.find(({ parentId }) => parentId === interactiveId),
    requiresGrading: doesInteractiveRequireGrade(getLatestAttemptActivityManualGradeReport),
  });

  return _.chain(response)
    .get('data.cohortById.enrollments.edges')
    .map(({ node }) => mapEnrollment(node))
    .keyBy('id')
    .value();
};
